import type { ScanAgentCapabilities, ScanRequest } from './scan-agent';

export type ScanOptions = Omit<ScanRequest, 'device'>;

/**
 * A first scan: 300 dpi, black and white, from the feeder, both sides, blank sides left out. Paige Scan's
 * own window starts the same way (`apps/scan/Paige.Scan/Scanning/ScanSettings.cs`, ScanSettings.Default).
 */
export const defaultScanOptions: ScanOptions = {
  dpi: 300,
  color: 'blackAndWhite',
  source: 'feeder',
  duplex: true,
  skipBlank: true,
};

/**
 * Only what the person chose is kept on this computer, per scanner, never a whole set of options: a
 * choice nobody made follows the defaults, and a new scanner starts from them. (`paige:scan-agent:options`,
 * before, kept the whole set for every scanner and pinned the old 300 dpi colour on anyone who had
 * changed anything.)
 */
const CHOICES_KEY = 'paige:scan-agent:choices';

type Choices = Record<string, Partial<ScanOptions>>;

/** The defaults with this computer's own choices for this scanner over them. */
export function rememberedOptions(device: string): ScanOptions {
  return { ...defaultScanOptions, ...readChoices()[device] };
}

/** Keeps a choice the person made for this scanner, beside the ones made before. */
export function rememberChoice(device: string, change: Partial<ScanOptions>): void {
  const choices = readChoices();
  try {
    choices[device] = { ...choices[device], ...change };
    window.localStorage.setItem(CHOICES_KEY, JSON.stringify(choices));
  } catch {
    // A convenience only; the choice lasts for the sheet.
  }
}

function readChoices(): Choices {
  try {
    const raw = window.localStorage.getItem(CHOICES_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Choices)
      : {};
  } catch {
    return {};
  }
}

const DEVICE_KEY = 'paige:scan-agent:device';

/** The scanner this computer chose last, or '' (the first listed is then chosen). */
export function rememberedDevice(): string {
  try {
    const raw = window.localStorage.getItem(DEVICE_KEY);
    const parsed = raw ? (JSON.parse(raw) as { id?: unknown }) : null;
    return typeof parsed?.id === 'string' ? parsed.id : '';
  } catch {
    return '';
  }
}

export function rememberDevice(id: string): void {
  try {
    window.localStorage.setItem(DEVICE_KEY, JSON.stringify({ id }));
  } catch {
    // A convenience only; the choice lasts for the sheet.
  }
}

/**
 * The options as this scanner can take them: the flatbed when it has no feeder (and the feeder when
 * it has no flatbed), one side when it cannot do two, and its nearest resolution when it does not
 * offer the one chosen. The choice itself is kept, so the next scanner gets it back.
 */
export function fitOptions(
  options: ScanOptions,
  capabilities: ScanAgentCapabilities | null,
): ScanOptions {
  if (!capabilities) return options;
  const source = fitSource(options.source, capabilities);
  const duplex = options.duplex && capabilities.duplex && source === 'feeder';
  const dpi = nearest(options.dpi, capabilities.resolutions);
  if (source === options.source && duplex === options.duplex && dpi === options.dpi) return options;
  return { ...options, source, duplex, dpi };
}

function fitSource(
  source: ScanOptions['source'],
  capabilities: ScanAgentCapabilities,
): ScanOptions['source'] {
  if (source === 'feeder' && !capabilities.feeder && capabilities.flatbed) return 'flatbed';
  if (source === 'flatbed' && !capabilities.flatbed && capabilities.feeder) return 'feeder';
  return source;
}

/** The offered resolution nearest the one chosen (the lower on a tie); the chosen one when none is offered. */
function nearest(dpi: number, offered: number[]): number {
  if (offered.length === 0 || offered.includes(dpi)) return dpi;
  return offered.reduce((best, r) => {
    const closer = Math.abs(r - dpi) - Math.abs(best - dpi);
    return closer < 0 || (closer === 0 && r < best) ? r : best;
  });
}
